import { useState } from 'react';
import { formatMoney, formatTime } from '@/lib/format';
import { CASH_MOVEMENT_LABELS } from '@/lib/cashSession';
import type { CashMovement, CashMovementType } from '@/lib/types';

interface CashMovementsCardProps {
  movements: CashMovement[];
  currencySymbol: string;
  disabled?: boolean;
  onAdd: (type: CashMovementType, amount: number, note: string) => Promise<{ error: string | null }>;
  onRemove: (id: string) => Promise<{ error: string | null }>;
}

const TYPE_ICONS: Record<CashMovementType, string> = {
  entrada: 'ri-arrow-down-line',
  retiro: 'ri-arrow-up-line',
  devolucion: 'ri-arrow-go-back-line',
};

export default function CashMovementsCard({
  movements,
  currencySymbol,
  disabled,
  onAdd,
  onRemove,
}: CashMovementsCardProps) {
  const [type, setType] = useState<CashMovementType>('entrada');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const handleAdd = async () => {
    const value = Number(amount);
    if (!amount.trim() || !Number.isFinite(value) || value <= 0) {
      setMsg('Ingresa un monto válido.');
      return;
    }
    setBusy(true);
    setMsg(null);
    const res = await onAdd(type, value, note);
    setBusy(false);
    if (res.error) {
      setMsg(res.error);
      return;
    }
    setAmount('');
    setNote('');
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <h2 className="font-heading text-lg font-semibold text-foreground-950">
        Movimientos de efectivo
      </h2>
      <p className="mt-1 text-sm text-foreground-600">
        Registra entradas, retiros y devoluciones para calcular el efectivo esperado.
      </p>

      {!disabled && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[160px_1fr_1fr_auto] gap-2 items-end">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">Tipo</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as CashMovementType)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              <option value="entrada">Entrada</option>
              <option value="retiro">Retiro</option>
              <option value="devolucion">Devolución</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">Monto</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1">
              Nota (opcional)
            </label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ej: fondo para cambio"
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={busy}
            className="h-[42px] inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-add-line" /> Agregar
          </button>
        </div>
      )}

      {msg && <p className="mt-2 text-xs text-accent-700">{msg}</p>}

      {movements.length === 0 ? (
        <div className="mt-4 rounded-md border border-dashed border-background-300 p-6 text-center text-sm text-foreground-500">
          No hay movimientos registrados.
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-background-100">
          {movements.map((m) => (
            <li key={m.id} className="py-2.5 flex items-center gap-3">
              <span
                className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                  m.type === 'entrada'
                    ? 'bg-accent-100 text-accent-900'
                    : 'bg-secondary-100 text-secondary-900'
                }`}
              >
                <i className={`${TYPE_ICONS[m.type]} text-base`} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-950">{CASH_MOVEMENT_LABELS[m.type]}</p>
                <p className="text-xs text-foreground-500 truncate">
                  {formatTime(m.created_at)}
                  {m.note ? ` · ${m.note}` : ''}
                </p>
              </div>
              <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                {m.type === 'entrada' ? '+' : '-'}
                {formatMoney(Number(m.amount), currencySymbol)}
              </span>
              {!disabled && (
                <button
                  onClick={() => onRemove(m.id)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-accent-700 transition cursor-pointer"
                  title="Eliminar movimiento"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}